import { get, isEmpty } from 'lodash';
import { createSelector } from 'reselect';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { RT_ON_TRENDS_EXCLUDE_LIST } from '@utils/metrics';
import { getRuleId } from '@utils/filter/ruleUtils';
import { realTimeModeOnTrendsSelector } from '@store/common/selectors';
import { METRIC_FILTER_TYPES } from '@components/metrics-panel-hz/utils/metricFilterUtils';
import { ViewContexts } from './reducer';
import { SCOPE } from './types';

const selector = (state) => get(state, SCOPE);

export default selector;

export const metricFilterSelector = createSelector(selector, (state) => state.metricFilter);

export const metricFilterRulesSelector = createSelector(metricFilterSelector, (metricFilter) =>
  metricFilter.map((filter) => filter.rule),
);

// used to mark selected areas on timeseries
// only applicable to bound metric filters
export const metricFilterLookupSelector = createSelector(metricFilterSelector, (metricFilter) => {
  const lookup = {};
  metricFilter.forEach((filter) => {
    const type = get(filter, ['meta', 'type'], METRIC_FILTER_TYPES.bound);
    if (type === METRIC_FILTER_TYPES.bound) {
      lookup[filter.metric] = filter;
    }
  });
  return lookup;
});

// metric filter error code list
export const metricFilterErrorCodesSelector: any = createSelector(
  metricFilterSelector,
  (metricFilter) => {
    const lookup = {};
    metricFilter.forEach((filter) => {
      const type = get(filter, ['meta', 'type'], METRIC_FILTER_TYPES.bound);
      if (type === METRIC_FILTER_TYPES.error) {
        const { metric, rule } = filter;
        // to be compatible with eq rule format
        lookup[metric] = 'value' in rule ? [rule.value] : rule.values;
      }
    });
    return lookup;
  },
);

export const isErrorSFAppliedSelector = createSelector(
  metricFilterErrorCodesSelector,
  (errorCodeLookup) => Object.keys(errorCodeLookup).length > 0,
);

export const dimensionsSelector = createSelector(selector, (state) => state.dimensions);

export const dimensionsToNASelector = createSelector(selector, (state) => state.dimensionsToNA);

export const isMetricFilterAppliedSelector = createSelector(
  metricFilterSelector,
  (metricFilter) => !isEmpty(metricFilter),
);

export const rulesSelector = createSelector(selector, (state) => state.rules);

export const buildFilterSelector = createSelector(rulesSelector, (rules) => {
  const ruleObj = {};
  rules.forEach((rule) => (ruleObj[getRuleId(rule)] = rule));
  const filter = getFilterFromRules(ruleObj, true);
  return filter;
});

// active filter rules
export const ruleSetSelector = createSelector(rulesSelector, (rules) => {
  const ruleSet = new Set();
  rules.forEach((rule) => ruleSet.add(getRuleId(rule)));
  return ruleSet;
});

export const compareSelector = createSelector(selector, (state) => state.compare);

export const compareFiltersSelector = createSelector(compareSelector, (state) => state.filters);

export const compareTypeSelector = createSelector(compareSelector, (state) => state.type);

export const activeFilterIndexSelector = createSelector(
  compareSelector,
  (state) => state.activeFilterIndex,
);

export const activeFilterSelector = createSelector(compareSelector, (state) =>
  get(state.filters, [state.activeFilterIndex], null),
);

export const trendsBaseSharingSelector = createSelector(
  selector,
  ({
    metricFilter,
    dimensions,
    dimensionPanelActiveTab,
    globalOrderBy,
    dimensionMetrics,
    compareFilters,
  }) => ({
    metricFilter,
    dimensions,
    dimensionPanelActiveTab,
    globalOrderBy,
    dimensionMetrics,
    compareFilters,
  }),
);

export const viewContextSelector = createSelector(selector, (state) => state.viewContext);

export const dimensionPanelActiveTabSelector = createSelector(
  selector,
  (state) => state.dimensionPanelActiveTab,
);

export const compareDrawerTitleSelector = createSelector(viewContextSelector, (context) =>
  context === ViewContexts.RT_FILTERS ? 'Real-Time: Filters' : 'Compare Filters',
);

export const filterDrawerTitleSelector = createSelector(viewContextSelector, (context) =>
  context === ViewContexts.RT_TRENDS ? 'Real-Time: Trends' : 'Filter Builder',
);

export const globalOrderBySelector = createSelector(selector, (state) => state.globalOrderBy);

// Create internal selectors to avoid re-rendering when other state changes
const internelDimensionMetricsSelector = createSelector(
  selector,
  (state) => state.dimensionMetrics,
);

const internelGlobalOrderBySelector = createSelector(selector, (state) => state.globalOrderBy);

export const dimensionMetricsSelector: any = createSelector(
  internelDimensionMetricsSelector,
  internelGlobalOrderBySelector,
  realTimeModeOnTrendsSelector,
  (dimensionMetrics, globalOrderBy, realTimeOnTrends) => {
    const metrics = realTimeOnTrends
      ? dimensionMetrics.filter((metric) => !RT_ON_TRENDS_EXCLUDE_LIST.includes(metric))
      : [...dimensionMetrics];
    if (!metrics.includes(globalOrderBy)) {
      metrics.push(globalOrderBy);
    }
    return metrics;
  },
);

export const dimensionsMetricsToNASelector = createSelector(
  selector,
  (store) => store.dimensionsMetricsToNA,
);

export const compareOnTrendsFiltersSelector = createSelector(
  selector,
  (store) => store.compareFilters,
);

export const compareOnTrendsFiltersMetaSelector = createSelector(
  compareOnTrendsFiltersSelector,
  (compareFilters) => {
    const metaMap = new Map();
    compareFilters.forEach((f) => {
      metaMap.set(f.rule, f.meta);
    });
    return metaMap;
  },
);

export const compareOnTrendsModeSelector = createSelector(
  compareOnTrendsFiltersSelector,
  (filters) => filters && filters.length > 0,
);

// flatten all compare filter rules for highlighting
export const compareRuleSetSelector = createSelector(compareOnTrendsFiltersSelector, (filters) => {
  const ruleSet = {};
  filters.forEach((filter, idx) => {
    const rules = getRulesFromFilter(filter.rule, true);
    Object.values(rules).forEach((rule) => {
      ruleSet[getRuleId(rule)] = idx;
    });
  });
  return ruleSet;
});

// only singular rules for toggle
export const quickCompareRuleSetSelector = createSelector(
  compareOnTrendsFiltersSelector,
  (filters) => {
    const quickRuleSet = {};
    filters.forEach((filter, idx) => {
      if (filter.rule.length === 1) {
        try {
          const rule = filter.rule[0][0];
          quickRuleSet[getRuleId(rule)] = idx;
        } catch (err) {
          //pass
        }
      }
    });
    return quickRuleSet;
  },
);
